import {
  Filters,
  WSButton,
  WSCheckboxGroup,
  WSDateInput,
  WSDateRangeSelectOptions,
  WSFilters,
  WSFlexBox,
  WSForm,
  WSPage,
  WSRadioInputGroup,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  IInvoice,
  IInvoiceTemplate,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import QueryString from "qs";
import { useMemo, useState } from "react";
import { Route, useHistory, useLocation } from "react-router-dom";
import { BrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import {
  useInvoicesQuery,
  useInvoiceTemplatesQuery,
  useMemberClientsQuery
} from "../../query/payments/queries";
import {
  getClientName,
  getClients,
  getInvoices,
  getInvoiceTemplates,
  getMemberClient
} from "../../query/payments/selectors";
import { WSQueries } from "../../query/WSQuery";
import { useSetWSStore, useWSStore } from "../../store";
import { isSerializedDate } from "../../utils/serviceHelper";
import { InvoicesTable } from "../components/InvoicesTable/InvoicesTable";
import { calculateInvoiceTempalteDueDate } from "../utils";
import { InvoicesInvoiceDetails } from "./InvoicesInvoiceDetails";
import { InvoicesInvoiceTemplateDetails } from "./InvoicesInvoiceTemplateDetails";
import styles from "./InvoicesList.module.scss";

enum InvoicesStatusFilter {
  Unsent = "Unsent",
  Open = "Open",
  Paid = "Paid"
}

enum InvoicesDateRangeFilter {
  All = "All",
  PastWeek = "PastWeek",
  PastMonth = "PastMonth",
  PastYear = "PastYear",
  Custom = "Custom"
}

interface InvoicesFilters extends Filters {
  status: InvoicesStatusFilter[];
  memberClientId: string;
  dueDateRange: InvoicesDateRangeFilter;
  customDueDateRange?: Date[];
}

function useUrlQueryFilters<F = Filters>(defaultFilters: F) {
  const history = useHistory();
  const location = useLocation();
  const store = useWSStore();
  const setStore = useSetWSStore();

  return {
    defaultFilters,
    filters: {
      ...defaultFilters,
      ...(location.search
        ? ((QueryString.parse(location.search.slice(1), {
            decoder: function(str, defaultDecoder, charset) {
              const decodedValue = defaultDecoder(str, defaultDecoder, charset);
              if (isSerializedDate(decodedValue)) {
                return new Date(decodedValue);
              } else {
                return decodedValue;
              }
            }
          }) as any) as F)
        : {})
    },
    setFilters: (newFilters: F) => {
      history.push({
        search: QueryString.stringify(newFilters)
      });
      setStore({
        savedFilters: {
          ...store.savedFilters,
          [location.pathname]: newFilters
        }
      });
    }
  };
}

export const InvoicesList = () => {
  const history = useHistory();
  const invoicesQuery = useInvoicesQuery();
  const invoiceTemplatesQuery = useInvoiceTemplatesQuery();
  const memberClientsQuery = useMemberClientsQuery();
  const { defaultFilters, filters, setFilters } = useUrlQueryFilters<
    InvoicesFilters
  >({
    status: [
      InvoicesStatusFilter.Unsent,
      InvoicesStatusFilter.Open,
      InvoicesStatusFilter.Paid
    ],
    memberClientId: "all",
    dueDateRange: InvoicesDateRangeFilter.All,
    customDueDateRange: []
  });
  const [search, setSearch] = useState("");
  const setStore = useSetWSStore();

  const clients = getClients(memberClientsQuery.data || []);

  const createInvoiceHandler = () => {
    setStore({ invoiceFormBackPath: "/member/invoices/list" });
    history.push("/member/invoices/create");
  };

  const dueDateRange = useMemo(() => {
    switch (filters.dueDateRange) {
      case InvoicesDateRangeFilter.Custom: {
        return filters.customDueDateRange;
      }
      case InvoicesDateRangeFilter.PastWeek: {
        return WSDateRangeSelectOptions.PastWeek.range;
      }
      case InvoicesDateRangeFilter.PastMonth: {
        return WSDateRangeSelectOptions.PastMonth.range;
      }
      case InvoicesDateRangeFilter.PastYear: {
        return WSDateRangeSelectOptions.Past12Months.range;
      }
      default:
        return undefined;
    }
  }, [filters.dueDateRange, filters.customDueDateRange]);

  const invoiceStatusFilter = (invoice: IInvoice) => {
    // Status filter

    let statusFilter = false;

    if (filters.status.includes(InvoicesStatusFilter.Unsent)) {
      statusFilter =
        statusFilter ||
        invoice.status === InvoiceStatus.Draft ||
        invoice.status === InvoiceStatus.Pending;
    }

    if (filters.status.includes(InvoicesStatusFilter.Open)) {
      statusFilter =
        statusFilter ||
        invoice.status === InvoiceStatus.Open ||
        invoice.status === InvoiceStatus.Overdue;
    }

    if (filters.status.includes(InvoicesStatusFilter.Paid)) {
      statusFilter =
        statusFilter ||
        invoice.status === InvoiceStatus.Paid ||
        invoice.status === InvoiceStatus.PaymentInTransit;
    }

    if (!statusFilter) return false;

    // Member client filter

    let memberClientFilter = true;

    if (filters.memberClientId !== "all") {
      memberClientFilter = invoice.memberClientId === filters.memberClientId;
    }

    if (!memberClientFilter) return false;

    // Due date range filter

    let dueDateRangeFilter = true;

    if (dueDateRange) {
      dueDateRangeFilter =
        invoice.dueDate > dueDateRange[0] && invoice.dueDate < dueDateRange[1];
    }

    if (!dueDateRangeFilter) return false;

    // Search filter

    let searchFilter = true;

    if (search) {
      searchFilter = false;

      searchFilter =
        searchFilter ||
        String(invoice.labels?.projectName)
          .toLowerCase()
          .includes(search.toLowerCase());

      invoice.lineItems.forEach(lineItem => {
        searchFilter =
          searchFilter ||
          (lineItem.description || "")
            .toLowerCase()
            .includes(search.toLowerCase());
      });

      const memberClient = getMemberClient(
        clients,
        invoice.memberClientId as string
      );
      if (memberClient) {
        searchFilter =
          searchFilter ||
          memberClient.company?.toLowerCase().includes(search.toLowerCase()) ||
          memberClient.name?.toLowerCase().includes(search.toLowerCase()) ||
          memberClient.emailTo?.toLowerCase().includes(search.toLowerCase()) ||
          memberClient.emailCC?.some(email =>
            email?.toLowerCase().includes(search.toLowerCase())
          ) ||
          false;
      }
    }

    if (!searchFilter) return false;

    return true;
  };

  const FrequencyAndScheduleStatusFilter = (
    invoiceTemplate: IInvoiceTemplate
  ) => {
    // Status filter

    let status = false;

    if (filters.status.includes(InvoicesStatusFilter.Unsent)) {
      status = true;
    }

    if (!status) return false;

    // Member client filter

    let memberClientFilter = true;

    if (filters.memberClientId !== "all") {
      memberClientFilter =
        invoiceTemplate.invoiceData.memberClientId === filters.memberClientId;
    }

    if (!memberClientFilter) return false;

    // Due date range filter

    let dueDateRangeFilter = true;

    if (dueDateRange) {
      const dueDate = calculateInvoiceTempalteDueDate(invoiceTemplate);
      dueDateRangeFilter =
        dueDate > dueDateRange[0] && dueDate < dueDateRange[1];
    }

    if (!dueDateRangeFilter) return false;

    // Search filter

    let searchFilter = true;

    if (search) {
      searchFilter = false;

      searchFilter =
        searchFilter ||
        String(invoiceTemplate.invoiceData.labels?.projectName)
          .toLowerCase()
          .includes(search.toLowerCase());

      invoiceTemplate.invoiceData.lineItems.forEach(lineItem => {
        searchFilter =
          searchFilter ||
          (lineItem.description || "")
            .toLowerCase()
            .includes(search.toLowerCase());
      });

      const memberClient = getMemberClient(
        clients,
        invoiceTemplate.invoiceData.memberClientId as string
      );
      if (memberClient) {
        searchFilter =
          searchFilter ||
          memberClient.company?.toLowerCase().includes(search.toLowerCase()) ||
          memberClient.name?.toLowerCase().includes(search.toLowerCase()) ||
          memberClient.emailTo?.toLowerCase().includes(search.toLowerCase()) ||
          memberClient.emailCC?.some(email =>
            email?.toLowerCase().includes(search.toLowerCase())
          ) ||
          false;
      }
    }

    if (!searchFilter) return false;

    return true;
  };

  return (
    <>
      <Route
        path="/member/invoices/:listName/template/:invoiceTemplateId"
        component={InvoicesInvoiceTemplateDetails}
        exact
      />

      <Route
        path="/member/invoices/:listName/:invoiceId"
        component={InvoicesInvoiceDetails}
        exact
      />

      <BrowserPageTitle title="Invoices" />

      <WSPage
        title="All invoices"
        primaryButtonComponent={props => (
          <WSButton
            name="createInvoice"
            onClick={createInvoiceHandler}
            {...props}
          >
            Create invoice
          </WSButton>
        )}
        breadcrumb={{
          label: "Invoicing",
          icon: "back",
          onClick: () => history.push("/member/invoices")
        }}
      >
        <WSQueries queries={{ memberClientsQuery }}>
          {({ memberClientsQuery }) => {
            const clients = getClients(memberClientsQuery.data);

            return (
              <WSFilters
                mb="M"
                filters={filters}
                searchQuery={search}
                defaultFilters={defaultFilters}
                onFilter={newFilters =>
                  setFilters({
                    ...filters,
                    ...newFilters
                  })
                }
                onSearch={newSearch => setSearch(newSearch)}
              >
                <WSForm.Field
                  name="status"
                  label="Status"
                  mb="L"
                  component={WSCheckboxGroup}
                  componentProps={{
                    options: Object.keys(InvoicesStatusFilter).map(key => ({
                      label: key,
                      value: key
                    }))
                  }}
                />
                <WSForm.Field
                  name="memberClientId"
                  label="Client"
                  mb="L"
                  component={WSSelect}
                  componentProps={{
                    options: [
                      {
                        label: "All clients",
                        value: "all"
                      },
                      ...clients.map(client => ({
                        label: getClientName(client),
                        value: client.memberClientId,
                        searchText: `${client.emailTo} ${client.name} ${client.company}`
                      }))
                    ]
                  }}
                />
                <WSForm.Field
                  name="dueDateRange"
                  label="Due date range"
                  mb="L"
                  component={WSRadioInputGroup}
                  componentProps={{
                    options: [
                      {
                        label: "All time",
                        value: InvoicesDateRangeFilter.All
                      },
                      {
                        label: "Past week",
                        value: InvoicesDateRangeFilter.PastWeek
                      },
                      {
                        label: "Past month",
                        value: InvoicesDateRangeFilter.PastMonth
                      },
                      {
                        label: "Past year",
                        value: InvoicesDateRangeFilter.PastYear
                      },
                      {
                        label: (
                          <WSForm.Context>
                            {({ setValue }) => (
                              <WSFlexBox
                                alignItems="center"
                                className={styles.dateRange}
                              >
                                <WSText mr="S">From</WSText>
                                <WSForm.Field
                                  name="customDueDateRange"
                                  component={WSDateInput}
                                  componentProps={{
                                    range: true,
                                    placeholder: "Select date range",
                                    onFocus() {
                                      setValue(
                                        "dueDateRange",
                                        InvoicesDateRangeFilter.Custom
                                      );
                                    }
                                  }}
                                />
                              </WSFlexBox>
                            )}
                          </WSForm.Context>
                        ),
                        value: InvoicesDateRangeFilter.Custom
                      }
                    ]
                  }}
                />
              </WSFilters>
            );
          }}
        </WSQueries>

        <WSQueries queries={{ invoicesQuery, invoiceTemplatesQuery }}>
          {({ invoicesQuery, invoiceTemplatesQuery }) => {
            const invoices = getInvoices(invoicesQuery.data);

            const invoiceTemplates = getInvoiceTemplates(
              invoiceTemplatesQuery.data
            ).filter(
              invoiceTemplate =>
                !invoiceTemplate.labels?.parentInvoiceTemplateId
            );

            const filteredInvoices = invoiceStatusFilter
              ? invoices.filter(invoiceStatusFilter)
              : invoices;

            const filteredInvoiceTemplatess = FrequencyAndScheduleStatusFilter
              ? invoiceTemplates.filter(FrequencyAndScheduleStatusFilter)
              : invoiceTemplates;

            return filteredInvoices.length > 0 ||
              filteredInvoiceTemplatess.length > 0 ? (
              <InvoicesTable
                listName="list"
                invoices={filteredInvoices}
                invoiceTemplates={filteredInvoiceTemplatess}
                sort={(a, b) => {
                  const aDueDate =
                    a.data.invoice?.createdAt ||
                    (a.data.invoiceTemplate
                      ? calculateInvoiceTempalteDueDate(a.data.invoiceTemplate)
                      : new Date());

                  const bDueDate =
                    b.data.invoice?.dueDate ||
                    (b.data.invoiceTemplate
                      ? calculateInvoiceTempalteDueDate(b.data.invoiceTemplate)
                      : new Date());

                  return aDueDate > bDueDate ? -1 : 1;
                }}
              />
            ) : (
              <WSText>No invoices found.</WSText>
            );
          }}
        </WSQueries>
      </WSPage>
    </>
  );
};
